import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { monetizationAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ArrowLeft, Crown, CheckCircle, Sparkles, Eye, TrendingUp, Users, MousePointerClick, Mail, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const ProfileBoost = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [status, setStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Iter-221b — Auto-activate the highlight when the talent returns from
  // a successful Stripe checkout. The success URL Stripe redirects to is
  // /talent/profile-boost?paid=1&duration={d}&session_id={s}. Calling
  // /highlight/activate flips is_highlighted=true on talent_profiles.
  useEffect(() => {
    const paid = searchParams.get('paid');
    const duration = searchParams.get('duration');
    const sessionId = searchParams.get('session_id');
    if (paid === '1' && duration) {
      (async () => {
        try {
          await monetizationAPI.activateHighlight(duration, sessionId || undefined);
          toast.success('Boost activated! Your profile is now highlighted.');
          fetchData();
        } catch {
          toast.error("Payment succeeded but activation failed. We'll retry automatically — contact support if it doesn't appear within 5 minutes.");
        } finally {
          searchParams.delete('paid');
          searchParams.delete('duration');
          searchParams.delete('session_id');
          setSearchParams(searchParams, { replace: true });
        }
      })();
    } else if (paid === '0') {
      toast.info('Payment cancelled — no charge was made.');
      searchParams.delete('paid');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchData = async () => {
    try {
      const [pricingRes, statusRes, analyticsRes] = await Promise.all([
        monetizationAPI.getHighlightPricing(),
        monetizationAPI.getHighlightStatus(),
        monetizationAPI.getHighlightAnalytics().catch(() => ({ data: null })),
      ]);
      setPricing(pricingRes.data);
      setStatus(statusRes.data);
      setAnalytics(analyticsRes?.data || null);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (duration) => {
    setPurchasing(true);
    try {
      const res = await monetizationAPI.purchaseHighlight(duration);
      if (res.data?.checkout_url) {
        // Iter-221b — Navigate same-tab so the post-checkout redirect lands
        // back on /talent/profile-boost?paid=1 reliably (the prior
        // window.open(new tab) was being eaten by popup blockers on
        // mobile Safari, leaving talents stuck without a checkout).
        window.location.href = res.data.checkout_url;
        return;
      }
      // Stripe not configured (preview / staging) — activate directly so
      // the flow can be tested end-to-end without real payments.
      await monetizationAPI.activateHighlight(duration);
      toast.success('Profile highlight activated!');
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(msg);
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="profile-boost-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/talent/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 mb-4">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Boost Your Profile</h1>
          <p className="text-gray-500 max-w-md mx-auto">
            Stand out to clients with a highlighted profile. Get more visibility, more bookings, and higher earnings.
          </p>
          {/* Iter-225n — explicit free-to-work disclaimer */}
          <p
            className="mt-3 text-xs text-gray-600 max-w-md mx-auto bg-gray-50 border border-gray-200 rounded-md px-3 py-2"
            data-testid="boost-free-disclaimer"
          >
            Applying to gigs and getting booked is always free. This optional boost simply increases profile visibility.
          </p>
        </div>

        {/* Current Status */}
        {status?.is_highlighted && (
          <Alert className="mb-6 bg-amber-50 border-amber-300" data-testid="highlight-active-banner">
            <Crown className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <span className="font-semibold">Your profile is highlighted!</span>
              {' '}Expires in {status.days_remaining} day{status.days_remaining !== 1 ? 's' : ''}.
              You can extend your highlight below.
            </AlertDescription>
          </Alert>
        )}

        {/* Iter-227i — Profile Boost Analytics */}
        {analytics && (
          <Card className="mb-6 bg-white border-cyan-100 shadow-sm" data-testid="boost-analytics-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base text-gray-900 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-cyan-500" />
                  Your Boost Performance
                </CardTitle>
                <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
                  Last 30 days
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                See exactly what your Boost is generating. Numbers refresh every page load.
              </p>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  {
                    icon: Eye,
                    color: 'text-cyan-600 bg-cyan-50 border-cyan-100',
                    label: 'Profile Views',
                    value: analytics.windows.last_30_days.profile_views,
                    sub: `${analytics.windows.last_7_days.profile_views} in the last 7 days`,
                    testid: 'metric-profile-views',
                  },
                  {
                    icon: TrendingUp,
                    color: 'text-amber-600 bg-amber-50 border-amber-100',
                    label: 'Search Impressions',
                    value: analytics.windows.last_30_days.search_impressions ?? 0,
                    sub: `${analytics.windows.last_7_days.search_impressions ?? 0} in the last 7 days`,
                    testid: 'metric-search-impressions',
                  },
                  {
                    icon: MousePointerClick,
                    color: 'text-sky-600 bg-sky-50 border-sky-100',
                    label: 'Client Clicks',
                    value: analytics.windows.last_30_days.client_clicks,
                    sub: `${analytics.windows.last_7_days.client_clicks} in the last 7 days`,
                    testid: 'metric-client-clicks',
                  },
                  {
                    icon: Mail,
                    color: 'text-violet-600 bg-violet-50 border-violet-100',
                    label: 'Invitations',
                    value: analytics.windows.last_30_days.invitations,
                    sub: `${analytics.windows.last_7_days.invitations} in the last 7 days`,
                    testid: 'metric-invitations',
                  },
                  {
                    icon: Briefcase,
                    color: 'text-emerald-600 bg-emerald-50 border-emerald-100',
                    label: 'Bookings',
                    value: analytics.windows.last_30_days.bookings,
                    sub: `${analytics.windows.last_7_days.bookings} in the last 7 days`,
                    testid: 'metric-bookings',
                  },
                ].map((m) => (
                  <div
                    key={m.label}
                    className={`rounded-lg border ${m.color.split(' ').slice(1).join(' ')} p-3`}
                    data-testid={m.testid}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <m.icon className={`w-3.5 h-3.5 ${m.color.split(' ')[0]}`} />
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-600">
                        {m.label}
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 leading-tight">{m.value}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{m.sub}</p>
                  </div>
                ))}
              </div>
              {!status?.is_highlighted && (
                <p className="text-xs text-gray-500 mt-3">
                  These numbers reflect organic activity right now. <strong className="text-cyan-700">Boost your profile below</strong> to amplify them.
                </p>
              )}

              {/* Iter-227m — Profile views by source breakdown */}
              {analytics.windows.last_30_days.views_by_source && (
                <div className="mt-4 pt-4 border-t border-gray-100" data-testid="views-by-source-card">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-600">
                      Where your views came from
                    </span>
                    <span className="text-[10px] text-gray-400">Last 30 days · internal QA excluded from totals</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { key: 'client_view', label: 'Clients', testid: 'views-source-client' },
                      { key: 'agency_view', label: 'Agencies', testid: 'views-source-agency' },
                      { key: 'ai_recommendation_view', label: 'AI Matches', testid: 'views-source-ai' },
                      { key: 'public_discovery_view', label: 'Public Link', testid: 'views-source-public' },
                    ].map((row) => {
                      const v = analytics.windows.last_30_days.views_by_source?.[row.key] || 0;
                      return (
                        <div
                          key={row.key}
                          className="rounded-md bg-gray-50 border border-gray-100 px-2 py-1.5"
                          data-testid={row.testid}
                        >
                          <p className="text-[10px] text-gray-500 leading-tight">{row.label}</p>
                          <p className="text-lg font-semibold text-gray-900 leading-tight">{v}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Benefits */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Eye, label: '3x More Views', desc: 'Profile visibility' },
            { icon: TrendingUp, label: 'Top Results', desc: 'Search priority' },
            { icon: Users, label: 'More Bookings', desc: 'Client attention' },
            { icon: Sparkles, label: 'Gold Badge', desc: 'Premium look' },
          ].map((b, i) => (
            <Card key={i} className="bg-white border-gray-200">
              <CardContent className="p-4 text-center">
                <b.icon className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="font-semibold text-sm text-gray-900">{b.label}</p>
                <p className="text-xs text-gray-500">{b.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pricing Cards — Iter-221b: 3-up grid (24h/week/month) with
            "Most Popular" badge on the featured (24h $4.99) option to
            align with the promo modal + inline banner advertising. */}
        {pricing && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {pricing.options.map((opt) => {
              const featured = !!opt.featured;
              const best = !!opt.best_value;
              const cardClass = featured
                ? 'border-pink-400 ring-2 ring-pink-100'
                : best
                  ? 'border-amber-400 ring-2 ring-amber-100'
                  : 'border-gray-200';
              const badge = featured ? opt.badge || 'Most Popular' : best ? 'Best Value' : null;
              const badgeBg = featured ? 'bg-pink-500' : best ? 'bg-amber-500' : '';
              const headerBg = featured ? 'bg-pink-50' : best ? 'bg-amber-50' : 'bg-gray-50';
              const btnClass = featured
                ? 'bg-gradient-to-r from-amber-500 via-pink-500 to-purple-600 hover:opacity-90 text-white font-semibold'
                : best
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : 'bg-cyan-500 hover:bg-cyan-600 text-white';
              return (
                <Card
                  key={opt.id}
                  className={`relative overflow-hidden border-2 transition-all hover:shadow-lg bg-white ${cardClass}`}
                  data-testid={`pricing-card-${opt.id}`}
                >
                  {badge && (
                    <div className={`absolute top-0 right-0 ${badgeBg} text-white text-xs font-bold px-3 py-1 rounded-bl-lg`}>
                      {badge.toUpperCase()}
                    </div>
                  )}
                  <CardHeader className={`pb-2 ${headerBg}`}>
                    <CardTitle className="text-lg text-gray-900">{opt.name}</CardTitle>
                    <p className="text-sm text-gray-600">{opt.description}</p>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-gray-900" data-testid={`price-${opt.id}`}>${opt.price}</span>
                      <span className="text-gray-600 ml-1">
                        {' / '}
                        {opt.duration_days < 1
                          ? '24 hours'
                          : opt.duration_days === 1
                            ? '24 hours'
                            : `${opt.duration_days} days`}
                      </span>
                    </div>
                    <ul className="space-y-2 mb-6">
                      {pricing.benefits.map((b, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={`w-full ${btnClass}`}
                      onClick={() => handlePurchase(opt.id)}
                      disabled={purchasing}
                      data-testid={`purchase-btn-${opt.id}`}
                    >
                      {purchasing ? 'Processing...' : status?.is_highlighted ? 'Extend Highlight' : 'Get Highlighted'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* How It Works */}
        <Card className="bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="text-gray-900">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { step: '1', title: 'Choose Your Plan', desc: 'Pick a 24-hour, weekly, or monthly highlight.' },
                { step: '2', title: 'Get Highlighted', desc: 'A gold badge appears on your profile instantly.' },
                { step: '3', title: 'More Bookings', desc: 'Clients see you first in search results.' },
              ].map((s) => (
                <div key={s.step} className="text-center">
                  <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center mx-auto mb-3">
                    {s.step}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-1">{s.title}</h4>
                  <p className="text-sm text-gray-500">{s.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upsell to Premium */}
        <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#FF10F0]" />
                  Want Consistent Visibility?
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Upgrade to Premium for ongoing visibility and priority placement every day
                </p>
              </div>
              <Button 
                className="bg-gradient-to-r from-[#03DFFC] to-[#FF10F0] text-white hover:opacity-90"
                onClick={() => navigate('/talent/premium')}
              >
                Upgrade to Premium
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfileBoost;
